import {
  type FrameSizeT,
  type SheepBreedConfigT,
  type CattleBreedConfigT,
} from '@ruminati/types_v2/livestock_system'

import { CattleAgeClass } from './age-classes-cattle'
import { SheepAgeClass } from './age-classes-sheep'
import {
  CATTLE_BREEDS,
  CATTLE_BREED_CHARACTERISTICS,
  SHEEP_BREEDS,
  SHEEP_BREED_CHARACTERISTICS,
} from './breeds'

export type GrowthCurveConfig = {
  baseRates: typeof CATTLE_BASE_GROWTH_RATES | typeof SHEEP_BASE_GROWTH_RATES
  breedMultiplier: number
  frameSizeMultiplier: number
  matureWeight: number
}

export type MarketCondition = 'drought' | 'average' | 'good'

export const MARKET_CONDITION_MULTIPLIERS: Record<MarketCondition, number> = {
  drought: 0.7, // 30% reduction in growth
  average: 1.0, // Baseline
  good: 1.15, // 15% increase
}

// How many kg's per day would we expect this age class to grow
const CATTLE_BASE_GROWTH_RATES: Record<CattleAgeClass, number> = {
  [CattleAgeClass.CALF_UNWEANED]: 0.7,
  [CattleAgeClass.WEANERS]: 0.8,
  [CattleAgeClass.YEARLINGS]: 0.85,
  [CattleAgeClass.HEIFER_24_30M]: 0.45,
  [CattleAgeClass.HEIFER_30_36M]: 0.25,
  [CattleAgeClass.COW_FIRST_CALF]: 0.35,
  [CattleAgeClass.COW_MATURE]: 0.1,
  [CattleAgeClass.STEER_18_24M]: 0.9,
  [CattleAgeClass.STEER_24_36M]: 0.7,
  [CattleAgeClass.BULL_BREEDING]: 0.0,
}

const SHEEP_BASE_GROWTH_RATES: Record<SheepAgeClass, number> = {
  [SheepAgeClass.LAMB_UNWEANED]: 0.2, // 200g/day (high growth, milk fed)
  [SheepAgeClass.LAMB_WEANER]: 0.15, // 150g/day (post-weaning growth)
  [SheepAgeClass.HOGGET]: 0.15, // 150g/day (slowing down)
  [SheepAgeClass.MAIDEN_EWE]: 0.12, // 120g/day (approaching maturity)
  [SheepAgeClass.EWE_FIRST_LAMB]: 0.08, // 80g/day (growing + lactating)
  [SheepAgeClass.EWE_MATURE]: 0.02, // 20g/day (minimal growth)
  [SheepAgeClass.WETHER_12_24M]: 0.18, // 180g/day (castrated, good growth)
  [SheepAgeClass.WETHER_24M_PLUS]: 0.1, // 100g/day (slower, older)
  [SheepAgeClass.RAM_BREEDING]: 0.05, // 50g/day (minimal, mature)
}

export function getGrowthCurveConfig(
  config: CattleBreedConfigT | SheepBreedConfigT,
): GrowthCurveConfig {
  let breedDetails: (typeof CATTLE_BREEDS)[0] | (typeof SHEEP_BREEDS)[0] | undefined =
    CATTLE_BREEDS.find((b) => b.breed === config.breed)
  const isCattleBreed = breedDetails !== undefined

  if (!breedDetails) breedDetails = SHEEP_BREEDS.find((b) => b.breed === config.breed)

  const baseRates = isCattleBreed ? CATTLE_BASE_GROWTH_RATES : SHEEP_BASE_GROWTH_RATES

  // Get breed characteristics
  const breedChars = isCattleBreed
    ? CATTLE_BREED_CHARACTERISTICS[(breedDetails as (typeof CATTLE_BREEDS)[0]).speciesType]
    : SHEEP_BREED_CHARACTERISTICS[(breedDetails as (typeof SHEEP_BREEDS)[0]).primaryUse]

  // Frame size multiplier
  const frameSizeMultipliers: Record<FrameSizeT, number> = {
    small: 0.9,
    medium: 1.0,
    large: 1.1,
  }

  return {
    baseRates,
    breedMultiplier: breedChars.breedMultiplier,
    frameSizeMultiplier: frameSizeMultipliers[config.frameSize],
    matureWeight: config.avgMatureWeight,
  }
}

export type SeasonalConfig = {
  monthlyMultipliers: number[]
  location: string
}

const seasonalPatterns: Record<string, number[]> = {
  VIC: [0.7, 0.6, 0.7, 0.8, 0.9, 0.9, 0.8, 0.9, 1.2, 1.3, 1.1, 0.9],
  NSW: [0.8, 0.7, 0.8, 0.9, 1.0, 0.9, 0.8, 0.9, 1.2, 1.2, 1.0, 0.9],
  QLD: [1.1, 1.2, 1.1, 0.9, 0.7, 0.6, 0.6, 0.7, 0.8, 0.9, 1.0, 1.1],
  SA: [0.7, 0.6, 0.7, 0.8, 0.9, 0.9, 0.8, 0.9, 1.2, 1.2, 1.0, 0.9],
  WA: [0.8, 0.7, 0.8, 0.9, 1.0, 0.9, 0.8, 0.9, 1.1, 1.2, 1.0, 0.9],
  TAS: [0.6, 0.6, 0.7, 0.8, 0.9, 0.9, 0.8, 0.9, 1.1, 1.2, 1.0, 0.8],
  NT: [1.2, 1.3, 1.2, 1.0, 0.7, 0.6, 0.6, 0.7, 0.8, 0.9, 1.0, 1.1],
}

export function getSeasonalConfig(state: string): SeasonalConfig {
  return {
    monthlyMultipliers: (seasonalPatterns[state] || seasonalPatterns['NSW'])!,
    location: state,
  }
}

export function calculateDailyGrowth(
  ageClass: CattleAgeClass | SheepAgeClass,
  currentWeight: number,
  currentDate: Date,
  ageInMonths: number,
  growthConfig: GrowthCurveConfig,
  seasonalConfig: SeasonalConfig,
  marketCondition: MarketCondition = 'average',
): number {
  const isCattleBreed = ageClass in CattleAgeClass

  // 1. Base rate for age class
  // @ts-expect-error
  let baseRate = growthConfig.baseRates[ageClass]

  // 2. Apply breed multiplier
  const breedAdjusted = baseRate * growthConfig.breedMultiplier

  // 3. Apply frame size multiplier
  const frameAdjusted = breedAdjusted * growthConfig.frameSizeMultiplier

  // 4. Apply maturity taper (slow down as approaching mature weight)
  const maturityMultiplier = isCattleBreed
    ? getMaturityMultiplier(currentWeight, growthConfig.matureWeight, ageInMonths)
    : getMaturityMultiplierSheep(currentWeight, growthConfig.matureWeight, ageInMonths)
  const maturityAdjusted = frameAdjusted * maturityMultiplier

  // 5. Apply seasonal adjustment
  const conditionMultiplier = MARKET_CONDITION_MULTIPLIERS[marketCondition]

  const month = currentDate.getMonth()
  const seasonalMultiplier = seasonalConfig.monthlyMultipliers[month]!
  const finalRate = maturityAdjusted * seasonalMultiplier * conditionMultiplier

  return finalRate
}

function getMaturityMultiplierSheep(
  currentWeight: number,
  matureWeight: number,
  _ageInMonths: number,
): number {
  const percentOfMature = currentWeight / matureWeight

  // Sheep mature faster - more aggressive taper
  if (percentOfMature >= 0.95) return 0.05
  if (percentOfMature >= 0.9) return 0.25
  if (percentOfMature >= 0.85) return 0.45
  if (percentOfMature >= 0.75) return 0.7

  return 1.0
}

function getMaturityMultiplier(
  currentWeight: number,
  matureWeight: number,
  ageInMonths: number,
): number {
  const percentOfMature = currentWeight / matureWeight

  // Base multiplier from weight
  let multiplier = 1.0
  if (percentOfMature >= 0.97) multiplier = 0.05
  else if (percentOfMature >= 0.93) multiplier = 0.2
  else if (percentOfMature >= 0.87) multiplier = 0.4
  else if (percentOfMature >= 0.8) multiplier = 0.6
  else if (percentOfMature >= 0.7) multiplier = 0.8

  // Young cows (< 48 months) get a boost
  if (ageInMonths < 48 && percentOfMature < 0.95) {
    multiplier = Math.min(1.0, multiplier * 1.3) // 30% boost for young cows
  }

  return multiplier // Full growth rate
}
